/**
 * The reusable case-detail body. Used by:
 *   - CaseDetailPage   — full-route deep link at /cases/{caseId}
 *   - CaseDetailDrawer — side overlay on /cases that preserves the queue
 *
 * Keep the component pure — accepts caseId + an optional "embedded" hint
 * for tighter spacing inside a drawer.
 */
import { useEffect, useRef } from 'react'

import { Alert, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Kpi } from '@/components/ui/kpi'
import { Skeleton } from '@/components/ui/skeleton'

import { bucketColor, bucketLabel, date, dpd, humanizeCode, inr, inrFull, priorityColor } from '@/lib/format'

import { useCase, type DuesBreakup } from './api'
import { CaseActionsPanel } from './CaseActionsPanel'
import { ActivityTimeline } from './ActivityTimeline'
import { VisitHistoryCard } from './VisitHistoryCard'
import { CaseAllocationsCard } from '@/features/allocations/CaseAllocationsCard'
import { useLabel } from '@/lib/labels'
import { FreshnessBadge } from '@/features/freshness/FreshnessBadge'
import { useMe } from '@/features/auth/api'
import { useCaseLmsRefresh } from '@/features/lms/api'

export function CaseDetailBody({
  caseId,
  embedded = false,
}: {
  caseId: string
  embedded?: boolean
}) {
  const label = useLabel()
  const { data, isPending, isError } = useCase(caseId)
  const { data: me } = useMe()

  // Graviton tier: read this loan from the LMS once per case opened. Waits
  // for the case so it knows whether it was open going in — the refresh
  // itself only reports the status after. The drawer swaps caseId without
  // remounting, hence the check against the mutation's own caseId.
  const graviton = me?.tenantTier === 'GRAVITON'
  const status = data?.head.status
  const lms = useCaseLmsRefresh()
  const { mutate: refreshFromLms } = lms
  const lmsCheckedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!graviton || !status || lmsCheckedFor.current === caseId) return
    lmsCheckedFor.current = caseId
    refreshFromLms({ caseId, wasOpen: status === 'OPEN' })
  }, [graviton, status, caseId, refreshFromLms])
  const lmsForThis = lms.variables?.caseId === caseId
  const lmsNote = !lmsForThis
    ? null
    : lms.isPending
      ? 'Checking the LMS…'
      : lms.data?.refreshed
        ? 'Live from the LMS'
        : lms.data?.reason === 'lms_unavailable'
          ? 'LMS unreachable — showing the last stored figures'
          : lms.data?.reason === 'recent'
            ? 'Read from the LMS moments ago'
            : null
  const paidAtLender =
    lmsForThis && !!lms.variables?.wasOpen && lms.data?.caseStatus === 'CLOSED'

  return (
    <div className={embedded ? 'space-y-5' : 'mx-auto max-w-[1400px] space-y-5'}>
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">Case</p>
          <h1 className="mt-1 flex items-center gap-2 text-xl font-semibold tracking-tight text-navy-900">
            {data?.head.loanAccountNo ?? <Skeleton className="h-7 w-40" />}
            {data?.head.status && (
              <Badge tone={data.head.status === 'OPEN' ? 'info' : 'slate'}>{data.head.status}</Badge>
            )}
            {data?.head.priorityBand && <PriorityBadge band={data.head.priorityBand} />}
          </h1>
          <p className="mt-1 font-mono text-2xs text-muted-foreground">{caseId}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <FreshnessBadge />
          {lmsNote && <p className="text-2xs text-muted-foreground">{lmsNote}</p>}
        </div>
      </div>

      {paidAtLender && (
        <Alert variant="info">
          <AlertTitle className="mb-0">Paid at the lender — this case is now closed.</AlertTitle>
        </Alert>
      )}

      {isError && (
        <div className="rounded-md border border-negative/30 bg-negative-soft p-4 text-sm text-negative">
          Couldn't load the case. It may have been closed, you may lack CASE_VIEW, or your
          session may have expired — try signing out and back in.
        </div>
      )}

      {/* KPI strip */}
      <div className={embedded
        ? 'grid gap-3 sm:grid-cols-2'
        : 'grid gap-3 sm:grid-cols-2 lg:grid-cols-4'}>
        <Kpi label="DPD" value={data ? dpd(data.head.currentDpd) : '—'} hint="Days past due" loading={isPending} deltaSense="lower-is-better" />
        <Kpi label="Bucket" value={data ? bucketLabel(data.head.currentBucket) : '—'} hint="Current ageing band" loading={isPending} deltaSense="lower-is-better" />
        <Kpi label="Outstanding" value={data ? inr(data.head.currentOutstanding) : '—'} hint="Outstanding principal" loading={isPending} deltaSense="lower-is-better" />
        <Kpi label="Priority score" value={data ? String(data.head.priorityScore) : '—'} hint={data?.head.priorityBand ?? '—'} loading={isPending} deltaSense="higher-is-better" />
      </div>

      {/* Two-column workspace */}
      <div className={embedded
        ? 'grid gap-4'
        : 'grid gap-4 lg:grid-cols-[1fr_22rem] xl:grid-cols-[1fr_26rem]'}>
        <div className="space-y-4">
          {data && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Customer</CardTitle>
                  <CardDescription>Top-of-folder identity and contact details.</CardDescription>
                </CardHeader>
                <CardContent>
                  <DL>
                    <DLRow label="Name" value={<span className="font-medium text-navy-900">{data.head.customerName}</span>} />
                    <DLRow label="External ref" value={<span className="font-mono text-xs">{data.head.externalCustomerRef}</span>} />
                    <DLRow
                      label="Mobile"
                      value={data.head.mobile
                        ? <a href={`tel:${data.head.mobile}`} className="font-mono text-xs text-navy-700 hover:underline">{data.head.mobile}</a>
                        : '—'}
                    />
                    <DLRow label="Address" value={formatAddress(data.head.currentAddressJson)} />
                    <DLRow label="Father's name" value={data.head.fatherName ?? '—'} />
                    <DLRow
                      label="Father's mobile"
                      value={data.head.fatherMobileMasked
                        ? <span className="font-mono text-xs">{data.head.fatherMobileMasked}</span>
                        : '—'}
                    />
                  </DL>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Loan</CardTitle>
                </CardHeader>
                <CardContent>
                  <DL>
                    <DLRow label="Loan account" value={<span className="font-mono text-xs">{data.head.loanAccountNo}</span>} />
                    <DLRow label="Product" value={humanizeCode(data.head.productType)} />
                    <DLRow label="Disbursement amount" value={<span className="tabular-nums">{inrFull(data.head.sanctionAmount)}</span>} />
                    <DLRow label="EMI" value={<span className="tabular-nums">{inrFull(data.head.emiAmount)}</span>} />
                    <DLRow label="Tenure" value={data.head.tenureMonths != null ? `${data.head.tenureMonths} months` : '—'} />
                    <DLRow label="Interest rate" value={data.head.interestRate != null ? `${Number(data.head.interestRate).toFixed(2)}%` : '—'} />
                    <DLRow label="Disbursed on" value={date(data.head.disbursalDate)} />
                    {data.head.openReason && <DLRow label="Open reason" value={label('openReason', data.head.openReason)} />}
                  </DL>
                </CardContent>
              </Card>

              <DuesBreakupCard dues={data.dues} />
            </>
          )}

          <Card>
            <CardHeader>
              <CardTitle>DPD timeline</CardTitle>
              <CardDescription>Last 30 snapshots from the daily DPD feed.</CardDescription>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              {isPending && <div className="space-y-2 px-5 pb-4">{[0,1,2,3,4].map(i => <Skeleton key={i} className="h-7 w-full" />)}</div>}
              {data && data.dpdTimeline.length === 0 && (
                <p className="px-5 pb-4 text-sm text-muted-foreground">No snapshots yet.</p>
              )}
              {data && data.dpdTimeline.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
                      <tr>
                        <th className="px-5 py-2 font-medium">As of</th>
                        <th className="px-3 py-2 text-right font-medium">DPD</th>
                        <th className="px-3 py-2 font-medium">Bucket</th>
                        <th className="px-3 py-2 text-right font-medium">Outstanding</th>
                        <th className="px-3 py-2 text-right font-medium">Total overdue</th>
                        <th className="px-5 py-2 font-medium">Last payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.dpdTimeline.map((t) => (
                        <tr key={t.asOfDate} className="border-b border-slate-200 last:border-0 hover:bg-navy-50">
                          <td className="px-5 py-2 font-mono text-xs text-navy-900">{date(t.asOfDate)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <span
                              className="inline-flex h-5 min-w-[2rem] items-center justify-center rounded px-1.5 text-2xs font-semibold tabular-nums text-white"
                              style={{ background: bucketColor(t.bucket) }}
                            >
                              {t.dpd}
                            </span>
                          </td>
                          <td className="px-3 py-2"><Badge tone="slate">{bucketLabel(t.bucket)}</Badge></td>
                          <td className="px-3 py-2 text-right tabular-nums">{inr(t.outstandingPrincipal)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{inr(t.totalOverdue)}</td>
                          <td className="px-5 py-2 text-muted-foreground">{date(t.lastPaymentDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right rail */}
        <div className="space-y-4">
          {data?.head.status === 'OPEN' && (
            <CaseActionsPanel caseId={caseId} currentOutstanding={data.head.currentOutstanding} />
          )}
          <CaseAllocationsCard caseId={caseId} />
          <VisitHistoryCard caseId={caseId} />
          <ActivityTimeline caseId={caseId} />
        </div>
      </div>
    </div>
  )
}

/**
 * Compose the customers.current_address JSONB (line1/line2/city/state/
 * pincode/landmark) into one display line. Unknown shape or no address → "—".
 */
/**
 * EMI component breakup for the current dues.
 *
 * The amounts come from the newest DPD snapshot, NOT a live LMS balance —
 * so the as-of date sits in the header rather than buried, and the card
 * says so plainly. An agent quoting a stale payoff to a customer is the
 * failure mode this guards against.
 */
export function DuesBreakupCard({ dues }: { dues: DuesBreakup | null }) {
  const collected = Number(dues?.collectedSince ?? 0)
  const hasCollections = collected > 0
  return (
    <Card>
      <CardHeader>
        <CardTitle>Amount due</CardTitle>
        <CardDescription>
          {!dues
            ? 'Overdue breakup from the DPD feed.'
            : hasCollections
              ? `As of ${date(dues.asOfDate)} — the lender's figure. Collections since are shown below it, not deducted from it.`
              : `What we are asking for, as of ${date(dues.asOfDate)} — snapshot values, not a live balance.`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!dues && (
          <p className="text-sm text-muted-foreground">
            No DPD snapshot for this loan yet.
          </p>
        )}
        {dues && (
          <DL>
            {/* Six overdue components that genuinely sum to the total below.
                The old card opened with POS — principal left on the whole loan
                — then closed with the arrears total, so the column read as
                arithmetic that did not hold: ₹52,672 + 0 + 2 + 0 above a
                "total" of ₹62,801. POS is portfolio data and is not here. */}
            <DLRow label="Principal due" value={<span className="tabular-nums">{inrFull(dues.principalDue)}</span>} />
            <DLRow label="Interest due" value={<span className="tabular-nums">{inrFull(dues.interestDue)}</span>} />
            <DLRow label="Bounce charges" value={<span className="tabular-nums">{inrFull(dues.bounceCharges)}</span>} />
            <DLRow label="Late payment charges" value={<span className="tabular-nums">{inrFull(dues.latePaymentCharges)}</span>} />
            <DLRow label="Other penalties" value={<span className="tabular-nums">{inrFull(dues.otherPenalties)}</span>} />
            <DLRow label="Other fees" value={<span className="tabular-nums">{inrFull(dues.otherFees)}</span>} />
            <DLRow
              label="Total due"
              value={
                <span
                  className={
                    hasCollections
                      ? 'tabular-nums text-navy-900'
                      : 'tabular-nums font-semibold text-navy-900'
                  }
                >
                  {inrFull(dues.totalDue)}
                </span>
              }
            />
            {/* Only once money has landed — "Collected −₹0" on every case is noise. */}
            {hasCollections && (
              <>
                <DLRow
                  label="Collected since"
                  value={
                    <span className="tabular-nums text-positive">
                      − {inrFull(dues.collectedSince)}
                    </span>
                  }
                />
                <DLRow
                  label="Net expected"
                  value={
                    <span className="tabular-nums font-semibold text-navy-900">
                      {inrFull(dues.netExpected)}
                    </span>
                  }
                />
              </>
            )}
          </DL>
        )}
      </CardContent>
    </Card>
  )
}

function formatAddress(json: string | null): string {
  if (!json) return '—'
  try {
    const a = JSON.parse(json) as Record<string, unknown>
    const parts = ['line1', 'line2', 'city', 'state', 'pincode', 'landmark']
      .map((k) => a[k])
      .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    return parts.length > 0 ? parts.join(', ') : '—'
  } catch {
    return '—'
  }
}

function PriorityBadge({ band }: { band: string }) {
  const c = priorityColor(band)
  return (
    <span
      className="inline-flex items-center rounded-md border px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider"
      style={{ background: c.bg, color: c.fg, borderColor: 'transparent' }}
    >
      {band}
    </span>
  )
}

export function DL({ children }: { children: React.ReactNode }) {
  return <dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-2 text-sm">{children}</dl>
}

export function DLRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-2xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd>{value ?? '—'}</dd>
    </>
  )
}
